import { SbbDateInputElement } from "@sbb-esta/lyne-elements/date-input.js";
import { SbbDateInput } from "@sbb-esta/lyne-react/date-input";
import {
  SbbDatepicker,
  SbbDatepickerNextDay,
  SbbDatepickerPreviousDay,
  SbbDatepickerToggle,
} from "@sbb-esta/lyne-react/datepicker";
import { datePickerPlaceholder } from "ApiConstraints";
import { FormField } from "components/Forms/FormField";
import { dateFormatWithWeekday, generateGuid, parseDateLenient, parseLyneDate, tryFormatDate } from "helpers";
import { createLogger } from "logging";
import React, { RefObject, useMemo, useRef } from "react";

import { defaultSize } from "./LyneConstants";
import { LyneFieldError } from "./LyneFieldError";
import { LyneFormField } from "./LyneFormField";

const logger = createLogger("LyneDatePicker");

interface LyneDatePickerProps extends FormField {
  format?: string;
  className?: string;
  value: Date | undefined;
  min?: Date;
  max?: Date;
  placeholder?: string;
  error?: string;
  onChange: (value: Date | undefined) => void;
  onBlur?: () => void;
  size?: "s" | "m" | "l";
  forwardRef?: RefObject<SbbDateInputElement>;
}

export const LyneDatePicker: React.FC<LyneDatePickerProps> = ({
  name,
  className,
  value,
  label,
  onChange,
  onBlur,
  disabled,
  required,
  min,
  max,
  error,
  placeholder = datePickerPlaceholder,
  size = defaultSize,
  forwardRef,
  contextHelp,
}) => {
  const ref = useRef<SbbDateInputElement>(null);
  const refToUse = forwardRef || ref;
  const nameToUse = useMemo(() => name || generateGuid(), [name]);

  const onChangeDate = (value: string) => {
    if (value) {
      const lyneDate = parseLyneDate(value);
      const date = parseDateLenient(lyneDate);
      logger.debug(`${nameToUse}.onChangeDate(): value:='${value}' -> '${date}'`);
      if (date) {
        onChange(date);
        return;
      }
    }
    logger.debug(`onChangeDate(): empty -> undefined`);
    onChange(undefined);
  };

  return (
    <LyneFormField size={size} name={nameToUse} label={label} required={required} contextHelp={contextHelp}>
      <SbbDatepickerPreviousDay input={nameToUse} />
      <SbbDateInput
        required
        id={nameToUse}
        ref={refToUse}
        value={tryFormatDate(value, dateFormatWithWeekday)}
        className={className}
        disabled={disabled}
        placeholder={placeholder}
        min={min}
        max={max}
        onInput={() => {
          const value = refToUse.current?.value || "";
          const parsed = parseLyneDate(value, true);
          logger.debug(`${nameToUse}.onInput(): value:='${value}' -> '${parsed}'`);
          onChangeDate(parsed);
        }}
        onBlur={() => {
          onBlur?.();
          const value = refToUse.current?.value || "";
          const parsed = parseLyneDate(value, false);
          logger.debug(`${nameToUse}.onBlur(): value:='${value}' -> '${parsed}'`);
          onChangeDate(parsed);
        }}
      />

      <SbbDatepicker input={nameToUse} />
      <SbbDatepickerToggle input={nameToUse} datepicker={nameToUse} />
      <SbbDatepickerNextDay input={nameToUse} />
      <LyneFieldError error={error} />
    </LyneFormField>
  );
};
